import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useParams, useNavigate } from 'react-router-dom';
import { RoutePaths } from '@core/routes/RoutePaths';

import { useStyles } from './ExecutionsSideBar.styles';
import { useTheme } from '@mui/material/styles';
import { ThemeProps } from '@styles/theme';
import { Avatar } from '@shared/components/Avatar';

import DownIcon from '@assets/down-icon.svg';
import UpIcon from '@assets/up-icon.svg';
import GroupsIcon from '@assets/groups-icon.svg';
import ProceduresIcon from '@assets/neo-procedures-icon.svg';
import BackIcon from '@assets/arrow-back.svg';

import {
    useExecutionsGridUIStore,
    useUserUIStore,
    useExecutionsUIStore,
} from '@core/useStores';
import { IUserStore } from 'Users/stores';
import { Observer } from 'mobx-react';
import { SidebarFilterButton } from '@shared/components/SidebarFilterButton';

type SidebarProps = {
    fromGroup?: boolean;
    mode?: 'user' | 'procedure';
    procedureName?: string | null;
    procedureImage?: string | null;
    onBack?: () => void;
    onInsights?: () => void;
}

export const ExecutionsSideBar: React.FC<SidebarProps> = ({
    fromGroup = false,
    mode = 'user',
    procedureName = null,
    procedureImage = null,
    onBack,
    onInsights,
}) =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const navigate = useNavigate();
    const params = useParams();
    const { id } = params;

    const userUIStore = useUserUIStore();

    const executionsGridUIStore = useExecutionsGridUIStore();
    const executionsUIStore = useExecutionsUIStore();

    const [selectedUser, setSelectedUser] = useState<IUserStore | null>(null);
    const [userLoading, setUserLoading] = useState<boolean>(false);

    useEffect(() =>{
        if (mode !== 'user') {
            setSelectedUser(null);
            setUserLoading(false);

            return;
        }

        const loadUser = async () => {
            if (!id) {
                setSelectedUser(null);
                setUserLoading(false);
                executionsUIStore.setExecutionsUserContext({ id: null, name: null, role: null });

                return;
            }

            setUserLoading(true);
            const result = await userUIStore.loadUser({ id: id});

            if(result?.success){
                setSelectedUser(result.data);
                setUserLoading(false);
                executionsUIStore.setExecutionsUserContext({
                    id,
                    name: result.data?.fullName || `${result.data?.firstName ?? ''} ${result.data?.lastName ?? ''}`.trim() || null,
                    role: result.data?.roles?.[0]?.name ?? null,
                });

                return;
            }

            setSelectedUser(null);
            setUserLoading(false);
            executionsUIStore.setExecutionsUserContext({ id, name: null, role: null });
        };

        void loadUser();
    }, [executionsUIStore, id, mode, userUIStore])

    useEffect(() => {
        const visibleGroups = (selectedUser?.groups ?? []).map(group => ({ id: String(group.id), name: group.name }));

        if (visibleGroups.length) {
            setShowGroups(true);
        }
    }, [selectedUser]);

    useEffect(() => {
        if (mode === 'user' && selectedUser?.procedures?.length) {
            setShowProcedures(true);
        }
    }, [mode, selectedUser]);


    const goBack = () => {
        if (onBack) {
            onBack();

            return;
        }

        executionsGridUIStore.setParams({
            page: 0,
            actorIds: [],
            entityIds: [],
            fullData: false,
        });
        executionsGridUIStore.clearScopedEntityIds();
        executionsGridUIStore.clearData();
        executionsUIStore.clearData();
        if(fromGroup){
            navigate(`/${RoutePaths.groups}`);
        }else{
            navigate(`/${RoutePaths.users}`);
        }
    };

    const selectProcedure = (procedureId: string) =>{
        if(executionsGridUIStore.analyticsEntityId != procedureId){
            executionsGridUIStore.setParams({
                page: 0,
                entityIds: [procedureId],
                fullData: true,
            })
            executionsGridUIStore.setAnalyticsEntityId(procedureId);
        }else{
            executionsGridUIStore.setParams({
                page: 0,
                entityIds: [],
                fullData: false,
            })
            executionsGridUIStore.clearAnalyticsEntityId();
        }
    }

    const selectActor = (actorId: string | null) => {
        executionsGridUIStore.setAnalyticsActorId(
            executionsGridUIStore.analyticsActorId === actorId ? null : actorId,
        );
    };

    const showAllProcedures = () => {
        executionsGridUIStore.setParams({
            page: 0,
            entityIds: [],
            fullData: false,
        });
        executionsGridUIStore.clearAnalyticsEntityId();
    }

    const [showGroups, setShowGroups] = useState<boolean>(true);
    const [showProcedures, setShowProcedures] = useState<boolean>(true);

    return (
        <Observer>
            {() => {
                const visibleGroups = (selectedUser?.groups ?? []).map(group => ({ id: String(group.id), name: group.name }));
                const activeProcedureScope = (selectedUser?.procedures ?? []).map(procedure => ({ id: String(procedure.id), name: procedure.name }));
                const knownProcedures = executionsGridUIStore.knownProcedureOptions;
                const mergedProcedures = new Map<string, { id: string; name: string; disabled: false }>();

                knownProcedures.forEach(procedure => {
                    const procedureId = String(procedure.id);

                    mergedProcedures.set(procedureId, {
                        id: procedureId,
                        name: procedure.name,
                        disabled: false,
                    });
                });

                activeProcedureScope.forEach(procedure => {
                    const procedureId = String(procedure.id);

                    mergedProcedures.set(procedureId, {
                        id: procedureId,
                        name: procedure.name,
                        disabled: false,
                    });
                });

                const proceduresList = Array.from(mergedProcedures.values()).sort((left, right) => left.name.localeCompare(right.name));
                const actorOptions = executionsGridUIStore.procedureActorOptions ?? [];
                const sidebarTitle = mode === 'procedure'
                    ? (procedureName ?? executionsGridUIStore.selectedProcedureExecutions[0]?.entityName ?? 'Procedure sessions')
                    : `${selectedUser ? selectedUser.firstName : ''} ${selectedUser ? selectedUser.lastName : ''}`.trim();

                return (
                    <div className={styles.sideBarContent} style={{
                        visibility: executionsUIStore.selectedExecutionId ? 'collapse' : 'visible',
                        height: executionsUIStore.selectedExecutionId ? '0px' : '100%',
                    }}>
                        <div className={styles.headerWraper}>
                            <img src={BackIcon} onClick={goBack}/>
                            {mode === 'user' && (
                                <Avatar
                                    firstName={selectedUser ? selectedUser.firstName : ''}
                                    lastName={selectedUser ? selectedUser.lastName : ''}
                                    image={selectedUser?.avatar}
                                    className={styles.userAvatar}
                                />
                            )}
                            {mode === 'procedure' && (
                                <div className={styles.procedureAvatar}>
                                    {procedureImage
                                        ? <img src={procedureImage} className={styles.procedureAvatarImg} alt="" />
                                        : <img src={ProceduresIcon} className={styles.procedureAvatarPlaceholder} alt="" />
                                    }
                                </div>
                            )}
                            <span className={styles.fullNameContaier}>
                                {sidebarTitle}
                            </span>
                            {onInsights && (
                                <button
                                    type="button"
                                    className={styles.insightsBtn}
                                    onClick={onInsights}
                                    title="View analytics summary"
                                >
                                    Insights
                                </button>
                            )}
                        </div>
                        {mode === 'user' && (
                            <div className={styles.groupsWrapper}>
                                <div className={styles.groupsHeader} onClick={() => setShowGroups(!showGroups)}>
                                    <div className={styles.headerLabel}>
                                        <img src={GroupsIcon}/>
                                        <Typography variant="h6" fontSize={18}>
                                            Groups
                                        </Typography>
                                    </div>
                                    <img src={showGroups ? UpIcon : DownIcon}/>
                                </div>
                                { showGroups &&
                                    <div className={styles.groupsContainer}>
                                        {visibleGroups.map(group => (
                                            <div key={group.id} className={styles.passiveGroupContainer}>
                                                <span className={styles.groupLabel}>{group.name}</span>
                                            </div>
                                        ))}
                                        {userLoading && (
                                            <div className={styles.placeholderText}>Loading groups...</div>
                                        )}
                                        {!userLoading && selectedUser && !visibleGroups.length && (
                                            <div className={styles.placeholderText}>No groups assigned</div>
                                        )}
                                    </div>
                                }
                            </div>
                        )}
                        <div className={styles.proceduresWrapper}>
                            <div className={styles.proceduresHeader} onClick={() => setShowProcedures(!showProcedures)}>
                                <div className={styles.headerLabel}>
                                    <img src={mode === 'procedure' ? GroupsIcon : ProceduresIcon}/>
                                    <Typography variant="h6" fontSize={18}>
                                        {mode === 'procedure' ? 'Actors' : 'Assigned Procedures'}
                                    </Typography>
                                </div>
                                <img src={showProcedures ? UpIcon : DownIcon}/>
                            </div>
                            {showProcedures &&
                                <div className={styles.proceduresScroller}>
                                    <div className={styles.proceduresContainer}>
                                        {mode === 'procedure' ? (
                                            <>
                                                <SidebarFilterButton
                                                    className={styles.actorFilterButton}
                                                    onClick={() => selectActor(null)}
                                                    active={!executionsGridUIStore.analyticsActorId}
                                                >
                                                    <span className={styles.actorFilterContent}>
                                                        <span className={styles.actorFilterLabel}>All actors</span>
                                                        <span className={styles.actorFilterCount}>{`${executionsGridUIStore.allSelectedProcedureExecutions.length} sessions`}</span>
                                                    </span>
                                                </SidebarFilterButton>
                                                {(executionsGridUIStore.isExecutionsLoading || executionsGridUIStore.isActorsLoading) && (
                                                    <div className={styles.placeholderText}>Loading actors...</div>
                                                )}
                                                {!executionsGridUIStore.isExecutionsLoading && !executionsGridUIStore.isActorsLoading && actorOptions.map(actor => (
                                                    <SidebarFilterButton
                                                        key={actor.id}
                                                        className={styles.actorFilterButton}
                                                        onClick={() => selectActor(actor.id)}
                                                        active={executionsGridUIStore.analyticsActorId === actor.id}
                                                    >
                                                        <span className={styles.actorFilterContent}>
                                                            <span className={styles.actorFilterLabel}>{actor.label}</span>
                                                            <span className={styles.actorFilterCount}>{`${actor.count} sessions`}</span>
                                                        </span>
                                                    </SidebarFilterButton>
                                                ))}
                                                {!executionsGridUIStore.isExecutionsLoading && !executionsGridUIStore.isActorsLoading && !actorOptions.length && (
                                                    <div className={styles.placeholderText}>No actors available</div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <SidebarFilterButton
                                                    className={styles.procedureContainer}
                                                    onClick={showAllProcedures}
                                                    active={!executionsGridUIStore.analyticsEntityId}
                                                >
                                                    All executions
                                                </SidebarFilterButton>
                                                {proceduresList.map(p =>
                                                    <SidebarFilterButton
                                                        key={p.id}
                                                        className={styles.procedureContainer}
                                                        onClick={() => selectProcedure(p.id)}
                                                        disabled={p.disabled}
                                                        active={executionsGridUIStore.isActive(p.id)}
                                                    >
                                                        {p.name}
                                                    </SidebarFilterButton>
                                                )}
                                                {userLoading && proceduresList.length === 0 && (
                                                    <div className={styles.placeholderText}>Loading procedures...</div>
                                                )}
                                                {!userLoading && proceduresList.length === 0 && (
                                                    <div className={styles.placeholderText}>No procedures available</div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                )
            }}
        </Observer>
    )
}